/**
 * What each surface offers each role: the feature contract.
 *
 * docs/product.md's matrix is rendered from this table (a test keeps them the
 * same), and the apps ask `offers` rather than keeping their own idea of what
 * exists where. A complex's capabilities, such as Telebirr, come from its own
 * document through `capabilitiesOf`.
 */

import type { CallableName } from "./contract.js";
import { EVERYTHING, type Entitlement, type Entitlements } from "./plans.js";

export type Surface = "phone" | "desktop" | "admin";
export type Role = "resident" | "accountant" | "committee" | "platform_admin" | "automatic";

export interface Feature {
  role: Role;
  label: string;
  surfaces: readonly Surface[];
  /** How it reaches the backend: callables, or a note such as "reads". */
  via: readonly (CallableName | `note:${string}`)[];
  /** The plan entitlement it needs, when it is one of a plan's extras. */
  needs?: Entitlement;
}

const reads = "note:reads";
const rules = "note:client write (rules)";

export const FEATURES = {
  // Resident
  residentBalance: {
    role: "resident",
    label: "See balance, months, history",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  residentSubmitPayment: {
    role: "resident",
    label: "Submit a payment with a reference",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  residentReceipt: {
    role: "resident",
    label: "Attach a receipt photograph",
    surfaces: ["phone", "desktop"],
    via: ["note:Storage upload"],
  },
  residentAnyAmount: {
    role: "resident",
    label: "Pay any amount or months ahead",
    surfaces: ["phone", "desktop"],
    via: ["verifyPaymentCall"],
  },
  residentGive: {
    role: "resident",
    label: "Give to a campaign",
    surfaces: ["phone", "desktop"],
    via: [rules],
    needs: "campaigns",
  },
  residentReportRepair: {
    role: "resident",
    label: "Report a repair",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  residentConfirmRepair: {
    role: "resident",
    label: "Confirm a repair was done",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  residentNotices: {
    role: "resident",
    label: "Read notices",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  residentChat: {
    role: "resident",
    label: "Talk in the building chat, under a chosen name",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  accountantAdvertisements: {
    role: "accountant",
    label: "Read advertisements",
    surfaces: ["phone", "desktop"],
    via: [reads],
    needs: "advertisements",
  },
  residentResetPassword: {
    role: "resident",
    label: "Reset a forgotten password by SMS",
    surfaces: ["phone", "desktop"],
    via: ["note:phone sign-in"],
  },
  residentFirstLogin: {
    role: "resident",
    label: "First login (phone confirmation)",
    surfaces: ["phone", "desktop"],
    via: ["completeFirstLoginCall"],
  },
  residentTelebirr: {
    role: "resident",
    label: "Pay with telebirr",
    surfaces: ["phone", "desktop"],
    via: ["startTelebirrPaymentCall", "checkTelebirrPaymentCall"],
    needs: "telebirr",
  },
  residentChapa: {
    role: "resident",
    label: "Pay with Chapa",
    surfaces: ["phone", "desktop"],
    via: ["startChapaPaymentCall", "checkChapaPaymentCall"],
  },
  // Accountant
  verifyPayments: {
    role: "accountant",
    label: "Verify or reject payments",
    surfaces: ["phone", "desktop"],
    via: ["verifyPaymentCall", "rejectPaymentCall"],
  },
  accountantChapa: {
    role: "accountant",
    label: "Chapa payments, confirmed by Chapa, and each one's receipt",
    surfaces: ["phone", "desktop"],
    via: [reads, "chapaReceiptCall"],
  },
  accountantUnits: {
    role: "accountant",
    label: "Units and their payments",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  recordCash: {
    role: "accountant",
    label: "Record a cash payment",
    surfaces: ["phone", "desktop"],
    via: ["recordPaymentCall"],
  },
  reversePayment: {
    role: "accountant",
    label: "Reverse a payment",
    surfaces: ["phone", "desktop"],
    via: ["reversePaymentCall"],
  },
  adjustBalance: {
    role: "accountant",
    label: "Adjust a balance",
    surfaces: ["phone", "desktop"],
    via: ["postAdjustmentCall"],
  },
  voidInvoice: {
    role: "accountant",
    label: "Void an invoice",
    surfaces: ["phone", "desktop"],
    via: ["voidInvoiceCall"],
  },
  raiseInvoicesNow: {
    role: "accountant",
    label: "Raise this month's invoices now",
    surfaces: [],
    via: ["raiseInvoices"],
  },
  resolveHeld: {
    role: "accountant",
    label: "Resolve held funds",
    surfaces: ["phone", "desktop"],
    via: ["resolveHeldFundsCall"],
  },
  recordExpense: {
    role: "accountant",
    label: "Record expenses",
    surfaces: ["phone", "desktop"],
    via: ["recordExpenseCall"],
  },
  voidExpense: {
    role: "accountant",
    label: "Void an expense",
    surfaces: ["phone", "desktop"],
    via: ["voidExpenseCall"],
  },
  reconcileAccount: {
    role: "accountant",
    label: "Reconcile an account balance",
    surfaces: ["phone", "desktop"],
    via: ["setAccountBalanceCall"],
  },
  changeUnitOrBuilding: {
    role: "accountant",
    label: "Change a unit or the building's due",
    surfaces: ["phone", "desktop"],
    via: ["changeUnitCall", "changeBuildingCall"],
  },
  sendReminders: {
    role: "accountant",
    label: "Send rent reminders",
    surfaces: ["phone", "desktop"],
    via: ["remindUnitsCall"],
    needs: "reminders",
  },
  books: {
    role: "accountant",
    label: "Books, arrears, journal, export",
    surfaces: ["phone", "desktop", "admin"],
    via: [reads],
  },
  issueSlips: {
    role: "accountant",
    label: "Issue sign-in slips",
    surfaces: ["admin"],
    via: ["issueSlipsCall"],
  },
  createResidents: {
    role: "accountant",
    label: "Create resident sign-ins",
    surfaces: ["admin"],
    via: ["createResidentCall"],
  },
  vouch: {
    role: "accountant",
    label: "Vouch for a resident",
    surfaces: ["admin"],
    via: ["vouchForResidentCall"],
  },
  resetResident: {
    role: "accountant",
    label: "Reset a resident's sign-in",
    surfaces: ["desktop", "admin"],
    via: ["resetPasswordCall"],
  },
  handOver: {
    role: "accountant",
    label: "Hand a unit over to a new occupant",
    surfaces: ["desktop"],
    via: ["handOverUnitCall"],
  },
  switchOff: {
    role: "accountant",
    label: "Switch an account off",
    surfaces: ["desktop"],
    via: ["setAccountActiveCall"],
  },
  addUnits: {
    role: "accountant",
    label: "Add units (used by the live seed)",
    surfaces: [],
    via: ["addUnitsCall"],
  },
  // Committee
  overview: {
    role: "committee",
    label: "Overview and unit map",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  committeeChapa: {
    role: "committee",
    label: "Money paid through Chapa, on its way and in the bank",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  campaigns: {
    role: "committee",
    label: "Open and close campaigns",
    surfaces: ["phone", "desktop"],
    via: ["createCampaignCall", "closeCampaignCall"],
    needs: "campaigns",
  },
  seeRepairs: {
    role: "committee",
    label: "See repairs and their states",
    surfaces: ["phone", "desktop"],
    via: [reads],
  },
  repairStates: {
    role: "committee",
    label: "Move repairs through their states",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  notices: {
    role: "committee",
    label: "Post notices",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  committeeChat: {
    role: "committee",
    label: "Run the building chat: delete, pin, turn chat off, lock, cooldown",
    surfaces: ["phone", "desktop"],
    via: [rules],
  },
  committeeJournal: {
    role: "committee",
    label: "Read the journal",
    surfaces: ["desktop", "admin"],
    via: [reads],
  },
  committeeResetResident: {
    role: "committee",
    label: "Reset a resident's sign-in, own building",
    surfaces: ["desktop"],
    via: ["resetPasswordCall"],
  },
  advertisements: {
    role: "committee",
    label: "Manage advertisements",
    surfaces: ["phone", "desktop"],
    via: [rules],
    needs: "advertisements",
  },
  // Platform admin
  telebirrMerchant: {
    role: "platform_admin",
    label: "A complex's telebirr merchant account",
    surfaces: ["admin"],
    via: ["setTelebirrMerchantCall"],
  },
  chapaSubaccount: {
    role: "platform_admin",
    label: "A committee's Chapa subaccount",
    surfaces: ["admin"],
    via: ["setChapaSubaccountCall"],
  },
  plans: {
    role: "platform_admin",
    label: "Plans, and which plan each complex is on",
    surfaces: ["admin"],
    via: ["savePlanCall", "setSubscriptionCall"],
  },
  complexes: {
    role: "platform_admin",
    label: "Create a complex, set pending/active/suspended",
    surfaces: ["admin"],
    via: ["createComplexCall", "setOrganizationStatusCall"],
  },
  staff: {
    role: "platform_admin",
    label: "Create staff, reset staff sign-ins",
    surfaces: ["admin"],
    via: ["createStaffCall", "resetPasswordCall"],
  },
  fixtureComplexes: {
    role: "platform_admin",
    label: "Hand-picked fixture complexes (seed tools)",
    surfaces: [],
    via: ["createOrganizationCall"],
  },
} as const satisfies Record<string, Feature>;

export type FeatureId = keyof typeof FEATURES;

/**
 * Whether a surface offers a feature to a role, in a complex whose plan
 * includes these extras. Without `entitlements` the plan is not considered.
 */
export function offers(
  feature: FeatureId,
  surface: Surface,
  role: string,
  entitlements: Entitlements = EVERYTHING,
): boolean {
  const spec: Feature = FEATURES[feature];
  return (
    spec.role === role &&
    spec.surfaces.includes(surface) &&
    (spec.needs === undefined || entitlements.has(spec.needs))
  );
}

/** What runs by itself, named here so the contract covers every function. */
export const AUTOMATIC = [
  { label: "Raise the month's invoices every morning", via: "raiseMonthlyInvoices" },
  { label: "Keep the monthly projection current", via: "rebuildUnitPeriod" },
  { label: "Notify on a payment decision", via: "notifyOnPaymentDecision" },
  { label: "Notify on a finished repair", via: "notifyOnRepairAttended" },
  { label: "Confirm a telebirr payment Telebirr reports", via: "telebirrNotify" },
  { label: "Confirm a Chapa payment Chapa reports", via: "chapaNotify" },
  { label: "Push an urgent notice to residents' phones", via: "pushUrgentNoticeOnPost" },
  { label: "Remind units still owing, monthly, if switched on", via: "sendAutomaticReminders" },
] as const;

/* ------------------------------------------------------------ capabilities */

/**
 * What a complex has switched on. Telebirr is off until the complex has
 * merchant credentials and the server-confirmed flow exists; with it off, the
 * apps show only the transfer-and-receipt path.
 */
export interface Capabilities {
  telebirr: boolean;
  /** On once the committee's Chapa subaccount is saved and switched on. */
  chapa: boolean;
}

export function capabilitiesOf(organization: Record<string, unknown> | undefined): Capabilities {
  const stored = organization?.capabilities;
  const flags =
    typeof stored === "object" && stored !== null ? (stored as Record<string, unknown>) : {};
  return { telebirr: flags.telebirr === true, chapa: flags.chapa === true };
}

/* ------------------------------------------------------------------ matrix */

const ROLE_HEADINGS: Record<Exclude<Role, "automatic">, string> = {
  resident: "Resident",
  accountant: "Accountant",
  committee: "Committee",
  platform_admin: "Platform admin",
};

/** The feature matrix in docs/product.md, rendered from the table above. */
export function featureMatrix(): string {
  const mark = (feature: Feature, surface: Surface) =>
    feature.surfaces.includes(surface) ? "✓" : "–";
  const via = (feature: Feature) =>
    feature.via
      .map((entry) => (entry.startsWith("note:") ? entry.slice(5) : `\`${entry}\``))
      .join(", ");
  const rows = [
    "| Feature | Phone | Desktop | Admin | Plan | Backend |",
    "| --- | :-: | :-: | :-: | --- | --- |",
  ];
  for (const [role, heading] of Object.entries(ROLE_HEADINGS)) {
    rows.push(`| **${heading}** | | | | | |`);
    for (const feature of Object.values(FEATURES) as Feature[]) {
      if (feature.role === role) {
        rows.push(
          `| ${feature.label} | ${mark(feature, "phone")} | ${mark(feature, "desktop")} | ${mark(feature, "admin")} | ${feature.needs ?? ""} | ${via(feature)} |`,
        );
      }
    }
  }
  rows.push("| **Automatic** | | | | | |");
  for (const job of AUTOMATIC) {
    rows.push(`| ${job.label} | | | | | \`${job.via}\` |`);
  }
  return rows.join("\n");
}
